import { SimpleLegalFooter } from "@/components/LegalFooter";
import { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Shield,
  Car,
  User,
  CreditCard,
  FileSignature,
  Check,
  ArrowRight,
  ArrowLeft,
  Search,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logo JYVIS
const JYVIS_LOGO = "/logo.svg";

// Logo TheGoodDeal.fr
const THEGOODDEAL_LOGO = "/images/thegooddeal.jpg";

const STEPS = {
  auto: ["vehicle", "coverage", "client", "payment", "summary"],
  moto: ["vehicle", "coverage", "client", "payment", "summary"],
  sante: ["client", "coverage", "payment", "summary"],
  prevoyance: ["client", "coverage", "payment", "summary"],
  rc_decennale: ["client", "coverage", "payment", "summary"],
  multirisque: ["property", "coverage", "client", "payment", "summary"],
  cybersecurite: ["company", "coverage", "client", "payment", "summary"],
  pret_immobilier: ["loan", "coverage", "client", "payment", "summary"],
  vie: ["client", "coverage", "payment", "summary"],
  retraite: ["client", "coverage", "payment", "summary"],
};

const STEP_LABELS = {
  vehicle: "Véhicule",
  property: "Logement",
  company: "Entreprise",
  loan: "Prêt",
  coverage: "Garanties",
  client: "Vos coordonnées",
  payment: "Paiement",
  summary: "Récapitulatif",
};

export const QUOTE_TYPES = Object.keys(STEPS);

export default function QuoteTunnel() {
  const { type } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const steps = STEPS[type] || STEPS.auto;
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  
  // Form data
  const [vehicleInfo, setVehicleInfo] = useState(location.state?.vehicleInfo || null);
  const [licensePlate, setLicensePlate] = useState("");
  const [coverageOptions, setCoverageOptions] = useState({
    type: "tiers",
    assistance_0km: false,
    protection_juridique: false,
    bris_glace: false,
    vol_incendie: false,
  });
  const [clientInfo, setClientInfo] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    birth_date: "",
    address: "",
    city: "",
    postal_code: "",
  });
  const [sepaMandate, setSepaMandate] = useState({
    iban: "",
    bic: "",
    account_holder: "",
    bank_name: "",
  });
  const [paymentFrequency, setPaymentFrequency] = useState("monthly");
  const [premiums, setPremiums] = useState({ monthly_premium: 0, annual_premium: 0 });

  // Brands and models
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedModel, setSelectedModel] = useState("");

  useEffect(() => {
    fetchBrands();
    if (vehicleInfo) {
      calculatePremium();
    }
  }, [type]);

  useEffect(() => {
    if (vehicleInfo || currentStep > 0) {
      calculatePremium();
    }
  }, [coverageOptions, vehicleInfo]);

  const fetchBrands = async () => {
    try {
      const vehicleType = type === "moto" ? "moto" : "auto";
      const res = await axios.get(`${API}/vehicle/brands?vehicle_type=${vehicleType}`);
      setBrands(res.data.brands);
    } catch (err) {
      console.error("Error fetching brands:", err);
    }
  };

  const fetchModels = async (brand) => {
    try {
      const vehicleType = type === "moto" ? "moto" : "auto";
      const res = await axios.get(`${API}/vehicle/models/${brand}?vehicle_type=${vehicleType}`);
      setModels(res.data.models);
    } catch (err) {
      console.error("Error fetching models:", err);
    }
  };

  const lookupPlate = async () => {
    if (!licensePlate.trim()) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/vehicle/lookup/${licensePlate}`);
      setVehicleInfo(res.data);
      setSelectedBrand(res.data.brand);
      fetchModels(res.data.brand);
      setSelectedModel(res.data.model);
      toast.success("Véhicule identifié !");
    } catch (err) {
      toast.error("Plaque non reconnue");
    }
    setLoading(false);
  };

  const handleBrandChange = (brand) => {
    setSelectedBrand(brand);
    fetchModels(brand);
    setSelectedModel("");
    setVehicleInfo(null);
  };

  const handleModelChange = (model) => {
    setSelectedModel(model);
    setVehicleInfo({
      brand: selectedBrand,
      model: model,
      year: new Date().getFullYear() - 3,
      power_cv: 100,
      fuel_type: "essence",
    });
  };

  const calculatePremium = async () => {
    // Simple client-side calculation for immediate feedback
    let base = type === "moto" ? 350 : 300;
    let multiplier = 1;

    if (coverageOptions.type === "tous_risques") multiplier = 2;
    else if (coverageOptions.type === "tiers_plus") multiplier = 1.4;

    let options = 0;
    if (coverageOptions.assistance_0km) options += 50;
    if (coverageOptions.protection_juridique) options += 40;
    if (coverageOptions.bris_glace) options += 60;
    if (coverageOptions.vol_incendie) options += 80;

    const annual = base * multiplier + options;
    setPremiums({
      monthly_premium: Math.round(annual / 12 * 100) / 100,
      annual_premium: Math.round(annual * 100) / 100,
    });
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const validateCurrentStep = () => {
    const step = steps[currentStep];
    
    if (step === "vehicle") {
      return vehicleInfo !== null;
    }
    if (step === "client") {
      return clientInfo.first_name && clientInfo.last_name && clientInfo.email && clientInfo.phone;
    }
    if (step === "payment") {
      return sepaMandate.iban && sepaMandate.account_holder;
    }
    return true;
  };

  const submitQuote = async () => {
    setLoading(true);
    try {
      const quoteData = {
        insurance_type: type,
        client_info: clientInfo,
        vehicle_info: vehicleInfo,
        coverage_options: coverageOptions,
        payment_frequency: paymentFrequency,
      };

      const res = await axios.post(`${API}/quotes`, quoteData);
      setQuote(res.data);
      
      // Create contract
      const contractData = {
        quote_id: res.data.id,
        client_id: res.data.client_id,
        sepa_mandate: sepaMandate,
      };
      
      await axios.post(`${API}/contracts`, contractData);
      
      toast.success("Votre demande a été enregistrée !");
      setCurrentStep(steps.length - 1);
    } catch (err) {
      console.error("Error submitting quote:", err);
      toast.error("Erreur lors de la soumission");
    }
    setLoading(false);
  };

  const progressPercent = ((currentStep + 1) / steps.length) * 100;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header - Blue */}
      <header className="bg-[#102692] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              {/* Logo animé avec bordure violette */}
              <div className="relative">
                <motion.div
                  className="absolute -inset-1 rounded-xl"
                  style={{
                    background: "conic-gradient(from 0deg, #8B5CF6, #A855F7, #D946EF, #EC4899, #8B5CF6)",
                  }}
                  animate={{ rotate: 360 }}
                  transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                />
                <div className="relative bg-white rounded-lg p-1">
                  <img src={JYVIS_LOGO} alt="JYVIS" className="h-8 w-auto" />
                </div>
              </div>
              <a href="https://thegooddeal.fr" target="_blank" rel="noopener noreferrer" className="hidden sm:block hover:opacity-80">
                <img src={THEGOODDEAL_LOGO} alt="TheGoodDeal.fr" className="h-6 w-auto rounded ml-3" />
              </a>
            </Link>
            <Button variant="ghost" onClick={() => navigate(-1)} className="text-white hover:bg-white/10" data-testid="back-home-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between mb-4">
            {steps.map((step, index) => (
              <div key={step} className="flex items-center">
                <div
                  className={`step-indicator ${
                    index < currentStep ? "completed" : index === currentStep ? "active" : "inactive"
                  }`}
                >
                  {index < currentStep ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </div>
                <span className={`ml-2 text-sm font-medium hidden sm:block ${
                  index <= currentStep ? "text-primary" : "text-slate-400"
                }`}>
                  {STEP_LABELS[step]}
                </span>
                {index < steps.length - 1 && (
                  <div className={`w-12 md:w-24 h-0.5 mx-2 ${
                    index < currentStep ? "bg-primary" : "bg-slate-200"
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="tunnel-progress">
            <div className="tunnel-progress-bar" style={{ width: `${progressPercent}%` }} />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {/* Vehicle Step */}
                {steps[currentStep] === "vehicle" && (
                  <Card data-testid="vehicle-step">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-3">
                        <Car className="w-6 h-6 text-primary" />
                        Informations sur votre véhicule
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* License Plate Lookup */}
                      <div>
                        <Label>Recherche par plaque d'immatriculation</Label>
                        <div className="flex gap-3 mt-2">
                          <div className="flex-1 relative">
                            <Input
                              placeholder="AB-123-CD"
                              value={licensePlate}
                              onChange={(e) => setLicensePlate(e.target.value.toUpperCase())}
                              className="plate-input h-12"
                              data-testid="tunnel-plate-input"
                            />
                          </div>
                          <Button
                            onClick={lookupPlate}
                            disabled={loading}
                            className="h-12"
                            data-testid="tunnel-lookup-btn"
                          >
                            <Search className="w-4 h-4 mr-2" />
                            Rechercher
                          </Button>
                        </div>
                      </div>

                      <div className="flex items-center">
                        <div className="flex-1 h-px bg-slate-200" />
                        <span className="px-4 text-sm text-slate-500">ou</span>
                        <div className="flex-1 h-px bg-slate-200" />
                      </div>

                      {/* Manual Selection */}
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Marque</Label>
                          <Select value={selectedBrand} onValueChange={handleBrandChange}>
                            <SelectTrigger className="mt-2" data-testid="brand-select">
                              <SelectValue placeholder="Sélectionner une marque" />
                            </SelectTrigger>
                            <SelectContent>
                              {brands.map((brand) => (
                                <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Modèle</Label>
                          <Select value={selectedModel} onValueChange={handleModelChange} disabled={!selectedBrand}>
                            <SelectTrigger className="mt-2" data-testid="model-select">
                              <SelectValue placeholder="Sélectionner un modèle" />
                            </SelectTrigger>
                            <SelectContent>
                              {models.map((model) => (
                                <SelectItem key={model} value={model}>{model}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Vehicle Info Display */}
                      {vehicleInfo && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="p-4 bg-green-50 border border-green-200 rounded-xl"
                        >
                          <div className="flex items-center gap-3 mb-2">
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                            <span className="font-semibold text-green-800">Véhicule identifié</span>
                          </div>
                          <p className="text-green-700">
                            {vehicleInfo.brand} {vehicleInfo.model} • {vehicleInfo.year} • {vehicleInfo.power_cv} CV • {vehicleInfo.fuel_type}
                          </p>
                        </motion.div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Coverage Step */}
                {steps[currentStep] === "coverage" && (
                  <Card data-testid="coverage-step">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-3">
                        <Shield className="w-6 h-6 text-primary" />
                        Choisissez vos garanties
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Coverage Type */}
                      <div>
                        <Label className="text-base font-semibold">Formule</Label>
                        <RadioGroup
                          value={coverageOptions.type}
                          onValueChange={(v) => setCoverageOptions({ ...coverageOptions, type: v })}
                          className="mt-3 space-y-3"
                        >
                          <div className="flex items-center space-x-3 p-4 border rounded-xl hover:border-primary/50 transition-colors">
                            <RadioGroupItem value="tiers" id="tiers" data-testid="coverage-tiers" />
                            <div className="flex-1">
                              <Label htmlFor="tiers" className="font-medium cursor-pointer">Au tiers</Label>
                              <p className="text-sm text-slate-500">Responsabilité civile obligatoire</p>
                            </div>
                            <span className="font-semibold text-primary">Base</span>
                          </div>
                          <div className="flex items-center space-x-3 p-4 border rounded-xl hover:border-primary/50 transition-colors">
                            <RadioGroupItem value="tiers_plus" id="tiers_plus" data-testid="coverage-tiers-plus" />
                            <div className="flex-1">
                              <Label htmlFor="tiers_plus" className="font-medium cursor-pointer">Tiers +</Label>
                              <p className="text-sm text-slate-500">+ Vol, incendie, bris de glace</p>
                            </div>
                            <span className="font-semibold text-primary">+40%</span>
                          </div>
                          <div className="flex items-center space-x-3 p-4 border border-primary/30 bg-primary/5 rounded-xl">
                            <RadioGroupItem value="tous_risques" id="tous_risques" data-testid="coverage-tous-risques" />
                            <div className="flex-1">
                              <Label htmlFor="tous_risques" className="font-medium cursor-pointer">Tous risques</Label>
                              <p className="text-sm text-slate-500">Protection maximale recommandée</p>
                            </div>
                            <span className="font-semibold text-accent">+100%</span>
                          </div>
                        </RadioGroup>
                      </div>

                      {/* Options */}
                      <div>
                        <Label className="text-base font-semibold">Options supplémentaires</Label>
                        <div className="mt-3 space-y-3">
                          {[
                            { key: "assistance_0km", label: "Assistance 0 km", price: "+50€/an" },
                            { key: "protection_juridique", label: "Protection juridique", price: "+40€/an" },
                            { key: "bris_glace", label: "Bris de glace", price: "+60€/an" },
                            { key: "vol_incendie", label: "Vol et incendie renforcé", price: "+80€/an" },
                          ].map((option) => (
                            <div
                              key={option.key}
                              className="flex items-center justify-between p-4 border rounded-xl hover:border-primary/30 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <Checkbox
                                  id={option.key}
                                  checked={coverageOptions[option.key]}
                                  onCheckedChange={(checked) =>
                                    setCoverageOptions({ ...coverageOptions, [option.key]: checked })
                                  }
                                  data-testid={`option-${option.key}`}
                                />
                                <Label htmlFor={option.key} className="cursor-pointer">{option.label}</Label>
                              </div>
                              <span className="text-sm text-slate-500">{option.price}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Client Step */}
                {steps[currentStep] === "client" && (
                  <Card data-testid="client-step">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-3">
                        <User className="w-6 h-6 text-primary" />
                        Vos coordonnées
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Prénom *</Label>
                          <Input
                            value={clientInfo.first_name}
                            onChange={(e) => setClientInfo({ ...clientInfo, first_name: e.target.value })}
                            className="mt-2"
                            data-testid="client-firstname"
                          />
                        </div>
                        <div>
                          <Label>Nom *</Label>
                          <Input
                            value={clientInfo.last_name}
                            onChange={(e) => setClientInfo({ ...clientInfo, last_name: e.target.value })}
                            className="mt-2"
                            data-testid="client-lastname"
                          />
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Email *</Label>
                          <Input
                            type="email"
                            value={clientInfo.email}
                            onChange={(e) => setClientInfo({ ...clientInfo, email: e.target.value })}
                            className="mt-2"
                            data-testid="client-email"
                          />
                        </div>
                        <div>
                          <Label>Téléphone *</Label>
                          <Input
                            type="tel"
                            value={clientInfo.phone}
                            onChange={(e) => setClientInfo({ ...clientInfo, phone: e.target.value })}
                            className="mt-2"
                            data-testid="client-phone"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Date de naissance</Label>
                        <Input
                          type="date"
                          value={clientInfo.birth_date}
                          onChange={(e) => setClientInfo({ ...clientInfo, birth_date: e.target.value })}
                          className="mt-2"
                          data-testid="client-birthdate"
                        />
                      </div>
                      <div>
                        <Label>Adresse</Label>
                        <Input
                          value={clientInfo.address}
                          onChange={(e) => setClientInfo({ ...clientInfo, address: e.target.value })}
                          className="mt-2"
                          data-testid="client-address"
                        />
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Ville</Label>
                          <Input
                            value={clientInfo.city}
                            onChange={(e) => setClientInfo({ ...clientInfo, city: e.target.value })}
                            className="mt-2"
                            data-testid="client-city"
                          />
                        </div>
                        <div>
                          <Label>Code postal</Label>
                          <Input
                            value={clientInfo.postal_code}
                            onChange={(e) => setClientInfo({ ...clientInfo, postal_code: e.target.value })}
                            className="mt-2"
                            data-testid="client-postalcode"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Payment Step */}
                {steps[currentStep] === "payment" && (
                  <Card data-testid="payment-step">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-3">
                        <CreditCard className="w-6 h-6 text-primary" />
                        Mode de paiement
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Payment Frequency */}
                      <div>
                        <Label className="text-base font-semibold">Fréquence de paiement</Label>
                        <RadioGroup
                          value={paymentFrequency}
                          onValueChange={setPaymentFrequency}
                          className="mt-3 grid grid-cols-2 gap-3"
                        >
                          <div className="flex items-center space-x-3 p-4 border rounded-xl hover:border-primary/50 transition-colors">
                            <RadioGroupItem value="monthly" id="monthly" data-testid="freq-monthly" />
                            <Label htmlFor="monthly" className="cursor-pointer">Mensuel</Label>
                          </div>
                          <div className="flex items-center space-x-3 p-4 border rounded-xl hover:border-primary/50 transition-colors">
                            <RadioGroupItem value="annual" id="annual" data-testid="freq-annual" />
                            <Label htmlFor="annual" className="cursor-pointer">Annuel (-5%)</Label>
                          </div>
                        </RadioGroup>
                      </div>

                      {/* SEPA Mandate */}
                      <div className="p-4 bg-slate-50 rounded-xl">
                        <h4 className="font-semibold mb-4 flex items-center gap-2">
                          <CreditCard className="w-5 h-5" />
                          Mandat de prélèvement SEPA
                        </h4>
                        <div className="space-y-4">
                          <div>
                            <Label>IBAN *</Label>
                            <Input
                              placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                              value={sepaMandate.iban}
                              onChange={(e) => setSepaMandate({ ...sepaMandate, iban: e.target.value })}
                              className="mt-2 font-mono"
                              data-testid="sepa-iban"
                            />
                          </div>
                          <div>
                            <Label>BIC</Label>
                            <Input
                              placeholder="BNPAFRPP"
                              value={sepaMandate.bic}
                              onChange={(e) => setSepaMandate({ ...sepaMandate, bic: e.target.value })}
                              className="mt-2"
                              data-testid="sepa-bic"
                            />
                          </div>
                          <div>
                            <Label>Titulaire du compte *</Label>
                            <Input
                              value={sepaMandate.account_holder}
                              onChange={(e) => setSepaMandate({ ...sepaMandate, account_holder: e.target.value })}
                              className="mt-2"
                              data-testid="sepa-holder"
                            />
                          </div>
                          <div>
                            <Label>Nom de la banque</Label>
                            <Input
                              value={sepaMandate.bank_name}
                              onChange={(e) => setSepaMandate({ ...sepaMandate, bank_name: e.target.value })}
                              className="mt-2"
                              data-testid="sepa-bank"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                        <div className="flex gap-3">
                          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-blue-800">
                            <p className="font-medium mb-1">Prélèvement sécurisé</p>
                            <p>Vos coordonnées bancaires sont protégées par le protocole SEPA européen. Le premier prélèvement aura lieu après signature du contrat.</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Summary Step */}
                {steps[currentStep] === "summary" && (
                  <Card data-testid="summary-step">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-3">
                        <FileSignature className="w-6 h-6 text-primary" />
                        Récapitulatif de votre demande
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {quote ? (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="text-center py-8"
                        >
                          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <CheckCircle2 className="w-10 h-10 text-green-600" />
                          </div>
                          <h3 className="text-2xl font-heading font-bold text-slate-900 mb-2">
                            Demande enregistrée !
                          </h3>
                          <p className="text-slate-600 mb-6">
                            Votre devis n° {quote.reference} a été créé avec succès.
                          </p>
                          <div className="p-4 bg-slate-50 rounded-xl mb-6 text-left">
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="text-slate-500">Prime mensuelle</span>
                                <p className="font-semibold text-lg">{quote.monthly_premium}€/mois</p>
                              </div>
                              <div>
                                <span className="text-slate-500">Prime annuelle</span>
                                <p className="font-semibold text-lg">{quote.annual_premium}€/an</p>
                              </div>
                            </div>
                          </div>
                          <p className="text-sm text-slate-500 mb-6">
                            Un email de confirmation vous a été envoyé. Notre équipe vous contactera sous 24h pour finaliser votre contrat avec signature électronique.
                          </p>
                          <div className="flex gap-4 justify-center">
                            <Button onClick={() => navigate("/")} variant="outline">
                              Retour à l'accueil
                            </Button>
                            <Button onClick={() => navigate("/espace-client")} className="bg-primary">
                              Mon espace client
                            </Button>
                          </div>
                        </motion.div>
                      ) : (
                        <>
                          {/* Summary Display */}
                          <div className="space-y-4">
                            {vehicleInfo && (
                              <div className="p-4 bg-slate-50 rounded-xl">
                                <h4 className="font-semibold mb-2">Véhicule</h4>
                                <p>{vehicleInfo.brand} {vehicleInfo.model} • {vehicleInfo.year}</p>
                              </div>
                            )}
                            <div className="p-4 bg-slate-50 rounded-xl">
                              <h4 className="font-semibold mb-2">Formule</h4>
                              <p className="capitalize">{coverageOptions.type.replace("_", " ")}</p>
                              {Object.entries(coverageOptions)
                                .filter(([k, v]) => k !== "type" && v)
                                .map(([k]) => (
                                  <span key={k} className="inline-block mr-2 mt-1 px-2 py-1 bg-primary/10 text-primary text-xs rounded">
                                    {k.replace(/_/g, " ")}
                                  </span>
                                ))}
                            </div>
                            <div className="p-4 bg-slate-50 rounded-xl">
                              <h4 className="font-semibold mb-2">Coordonnées</h4>
                              <p>{clientInfo.first_name} {clientInfo.last_name}</p>
                              <p className="text-sm text-slate-500">{clientInfo.email} • {clientInfo.phone}</p>
                            </div>
                          </div>

                          <div className="flex items-center gap-3 p-4 border border-primary/30 bg-primary/5 rounded-xl">
                            <Checkbox id="terms" data-testid="terms-checkbox" />
                            <Label htmlFor="terms" className="text-sm cursor-pointer">
                              J'accepte les conditions générales et la <Link to="/politique-de-confidentialite" className="text-primary underline">politique de confidentialité</Link>
                            </Label>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Navigation Buttons */}
            {!quote && (
              <div className="flex justify-between mt-6">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={currentStep === 0}
                  data-testid="prev-step-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Précédent
                </Button>
                
                {currentStep < steps.length - 1 ? (
                  <Button
                    onClick={handleNext}
                    disabled={!validateCurrentStep()}
                    className="bg-primary"
                    data-testid="next-step-btn"
                  >
                    Suivant
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={submitQuote}
                    disabled={loading}
                    className="bg-accent hover:bg-accent/90"
                    data-testid="submit-quote-btn"
                  >
                    {loading ? "Envoi en cours..." : "Valider ma demande"}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Sidebar - Quote Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-32" data-testid="quote-summary-card">
              <CardHeader className="bg-primary text-white rounded-t-xl">
                <CardTitle className="text-lg">Votre devis</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Type</span>
                    <span className="font-medium capitalize">{type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Formule</span>
                    <span className="font-medium capitalize">{coverageOptions.type.replace("_", " ")}</span>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex justify-between mb-2">
                      <span className="text-slate-500">Prime mensuelle</span>
                      <span className="font-bold text-xl text-primary">{premiums.monthly_premium}€</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Prime annuelle</span>
                      <span className="font-medium">{premiums.annual_premium}€</span>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <div className="flex items-center gap-2 text-green-800 text-sm">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Devis sans engagement</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <SimpleLegalFooter />
    </div>
  );
}
